import * as Sentry from '@sentry/angular';
import { environment as sharedEnvironment, User } from '@movinmotion/conf-shared';
import { EnvironmentProviders, ErrorHandler, inject, provideAppInitializer, Provider } from '@angular/core';
import { Router } from '@angular/router';

interface SentryConfig {
  project: {
    name: string;
  };
  pkg: {
    version: string;
  };
  dsn: string;
  environments: string[];
}

// MOV-17410 : Ajouter les paramètres contenant des infos personnelles au fil des détections
const anonymisedSentryValue = '[ANONYMOUS_SENTRY]';
const urlParamToSanitize = new Set(['email', 'password', 'userEmail']);
const bodyParamToSanitize = new Set([
  'email',
  'profile',
  'cv',
  'password',
  'password',
  'newPassword',
  'firstname',
  'lastname',
  'otp',
  'token',
]);

export function sanitizeUrlForSentry(url: string): string {
  try {
    const parsed = new URL(url);

    urlParamToSanitize.forEach(urlParam => {
      if (parsed.searchParams.has(urlParam)) {
        parsed.searchParams.set(urlParam, anonymisedSentryValue);
      }
    });

    return parsed.toString();
  } catch {
    return url; // fallback if URL parsing fails
  }
}

export function sanitizeBodyForSentry(body: any): any {
  if (body == null || typeof body !== 'object') return body;
  return Object.fromEntries(
    Object.entries(body).map(([key, value]) => [
      key,
      bodyParamToSanitize.has(key) ? anonymisedSentryValue : sanitizeBodyForSentry(value),
    ]),
  );
}

export function initSentry({ project, pkg, dsn, environments }: SentryConfig): void {
  if (environments.includes(sharedEnvironment.environment))
    Sentry.init({
      dsn,
      environment: sharedEnvironment.environment,
      release: `${project.name}@${pkg.version}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.breadcrumbsIntegration({
          // Désactivation de certaines options pour ne pas transmettre des données personnelles à Sentry
          console: false,
          dom: false,
          fetch: false,
          history: false,
          xhr: false,
          sentry: true,
        }),
      ],
      tracePropagationTargets: [...Object.values(sharedEnvironment.api)],
      tracesSampleRate: 1.0,
      sendDefaultPii: false, // Désactivé pour ne pas transmettre des données personnelles à Sentry
      beforeSend(event) {
        if (event.request?.url) {
          event.request.url = sanitizeUrlForSentry(event.request.url);
        }

        if (event.request?.data) {
          event.request.data = sanitizeBodyForSentry(event.request.data);
        }
        return event;
      },
    });
}

export function provideSentry(): Array<Provider | EnvironmentProviders> {
  return [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
        logErrors: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ];
}

export function updateSentryUser(user: User): void {
  Sentry.setUser({ id: user?.id?.toString() });
}
